/**
 * ClinicContext - 병원 선택 및 전역 상태 관리
 *
 * 권한별 동작:
 * - ADMIN: 여러 병원 조회 및 선택 가능
 * - POWER/USER: 자신의 병원만 조회
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { isAdmin } from '@/types/auth';
import { organizationsApi, type Organization, type ClinicFeatures } from '@/api/organizations';

// Organization을 Clinic으로 alias (호환성 유지)
type Clinic = Organization;

interface ClinicContextType {
  selectedClinic: Clinic | null;
  availableClinics: Clinic[];
  selectClinic: (clinic: Clinic) => void;
  isLoading: boolean;
  features: ClinicFeatures; // 현재 선택된 병원의 기능 플래그
}

const ClinicContext = createContext<ClinicContextType | undefined>(undefined);

export function ClinicProvider({ children }: { children: ReactNode }) {
  const { user } = useAuthStore();
  const [selectedClinic, setSelectedClinic] = useState<Clinic | null>(null);
  const [availableClinics, setAvailableClinics] = useState<Clinic[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadClinics = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // ADMIN: 모든 조직 조회
        if (isAdmin(user)) {
          const organizations = await organizationsApi.getAllOrganizations();
          setAvailableClinics(organizations);

          // 첫 번째 조직을 기본 선택
          if (organizations.length > 0 && !selectedClinic) {
            setSelectedClinic(organizations[0]);
          }
        } else {
          // POWER/USER: 자신의 조직만 조회
          const myOrganization = await organizationsApi.getMyOrganization();
          setAvailableClinics([myOrganization]);
          setSelectedClinic(myOrganization);
        }
      } catch {

        // 에러 발생 시 사용자 이름을 기본값으로 사용
        const fallbackClinic: Clinic = {
          id: 0,
          code: 'fallback',
          name: user.name || '나의 조직',
          businessName: user.name,
          features: { outbound: false, aiConsult: false, analytics: false }
        };
        setAvailableClinics([fallbackClinic]);
        setSelectedClinic(fallbackClinic);
      } finally {
        setIsLoading(false);
      }
    };

    loadClinics();
  }, [user]);

  const selectClinic = (clinic: Clinic) => {
    setSelectedClinic(clinic);
  };

  return (
    <ClinicContext.Provider
      value={{
        selectedClinic,
        availableClinics,
        selectClinic,
        isLoading,
        // 현재 선택된 병원의 기능 플래그 (기본값 보장)
        features: selectedClinic?.features || { outbound: false, aiConsult: false, analytics: false },
      }}
    >
      {children}
    </ClinicContext.Provider>
  );
}

export function useClinic() {
  const context = useContext(ClinicContext);
  if (context === undefined) {
    throw new Error('useClinic must be used within a ClinicProvider');
  }
  return context;
}
